/**
 * Client HTTP unique de l'application.
 *
 * Typé par le contrat OpenAPI généré (`generated.ts`) : un champ renommé côté
 * backend devient une erreur de compilation TypeScript, pas un bug en production
 * (docs/08 §11). Aucun `fetch` direct ailleurs dans le code.
 */
import createClient from "openapi-fetch";

import { API_BASE_URL, DEFAULT_HEADERS } from "@/shared/api/config";
import { toApiError } from "@/shared/api/errors";
import { sendRequest } from "@/shared/api/http";
import type { paths } from "@/shared/api/generated";
import { refreshSession, useSessionStore } from "@/shared/auth/session";

export { API_BASE_URL };

/**
 * Endpoints qu'il ne faut PAS rejouer après un 401.
 *
 * `login` répond 401 sur un mauvais mot de passe : rafraîchir puis rejouer
 * n'aurait aucun sens et masquerait l'erreur. `refresh` est déjà la rotation.
 */
const NO_RETRY = ["/api/v1/auth/login", "/api/v1/auth/refresh"];

function withBearer(request: Request, token: string | null): Request {
  if (token === null) {
    return request;
  }
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return new Request(request, { headers });
}

/**
 * `fetch` de l'application : ajoute le jeton, et rejoue une fois après rotation.
 *
 * L'intercepteur vit ici plutôt que dans chaque hook : un seul endroit sait
 * qu'un 401 peut être rattrapable, et un seul endroit décide de renoncer.
 */
async function authenticatedFetch(request: Request): Promise<Response> {
  const token = useSessionStore.getState().accessToken;
  // Cloné AVANT l'envoi : une fois la requête consommée, son corps ne peut plus
  // être relu, et le rejeu partirait sans charge utile.
  const replay = request.clone();

  const response = await sendRequest(withBearer(request, token));
  if (response.status !== 401 || NO_RETRY.some((path) => request.url.includes(path))) {
    return response;
  }

  const renewed = await refreshSession();
  if (renewed === null) {
    return response;
  }
  return sendRequest(withBearer(replay, renewed));
}

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  headers: DEFAULT_HEADERS,
  credentials: "include",
  fetch: authenticatedFetch,
});

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Extrait les données d'une réponse `openapi-fetch` ou lève une `ApiError`.
 *
 * Centralisé ici pour que chaque hook n'ait pas à réimplémenter la gestion
 * d'erreur — et pour que TanStack Query reçoive toujours une `ApiError` typée.
 *
 * Le succès se juge sur `response.ok`, **pas** sur la présence de données : un
 * `204 No Content` n'a pas de corps, et le traiter comme un échec ferait passer
 * pour cassés la déconnexion, la vérification d'email et la réinitialisation de
 * mot de passe — qui viennent pourtant de réussir.
 */
export async function unwrap<T>(request: Promise<FetchResult<T>>): Promise<T> {
  const result = await request;
  if (!result.response.ok || result.data === undefined) {
    throw toApiError(result.error, result.response.status);
  }
  return result.data;
}

/** Variante pour les endpoints qui répondent `204 No Content`. */
export async function expectNoContent(request: Promise<FetchResult<unknown>>): Promise<void> {
  const result = await request;
  if (!result.response.ok) {
    throw toApiError(result.error, result.response.status);
  }
}
